import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Info, Loader2 } from "lucide-react";

import { ButtonTip } from "@/components/ui/button-tip";

import ResultTiles from "../ResultTiles";
import { Collapse } from "../calm";
import { filesOfSlot, fullName, LOGO_SLOT } from "../onboardingModel";
import { StepBody, StepFooter, StepHeading } from "../StepCard";
import { numberedCount, type StepDef } from "../steps";
import { slotTitle } from "./SlotStep";
import type { StepContext } from "./shared";

/** A count that runs up to its value: the number of files is real, so it may count. */
function Count({ value }: { value: number }) {
  return <span className="tabular-nums">{value}</span>;
}

/** The button that sends the data stays quiet for this long after the step is entered, so a key still held down cannot send it. */
export const SUBMIT_ARM_MS = 600;

/** Step 8: everything on one calm page, what comes of it, and the one button that sends it. */
export default function ReviewStep({ ctx, onSubmit }: { ctx: StepContext; onSubmit: () => void }) {
  const { c } = ctx;
  const { t } = useTranslation();
  const view = c.view!;
  const values = c.values!;
  const profile = values.profile;
  const answers = values.answers;
  const dataSlots = view.slots.filter((slot) => slot.slot !== LOGO_SLOT);
  const edit = (step: StepDef | undefined) => step && ctx.goTo(step);
  const stepFor = (kind: StepDef["kind"], slot?: string) => ctx.steps.find((candidate) => candidate.kind === kind && (!slot || candidate.slot === slot));
  const optionalStep = ctx.steps.find((candidate) => candidate.kind === "optional");
  const answerRows: [string, string | undefined][] = [
    ["stableIds", answers.stableIds ? t(`onboardingPage.choice.${answers.stableIds}`) : undefined],
    ["labelsInstalled", answers.labelsInstalled ? t(`onboardingPage.choice.${answers.labelsInstalled}`) : undefined],
    ["labelNumberNote", answers.labelsInstalled === "yes" ? answers.labelNumberNote : undefined],
    ["criticalAreas", answers.criticalAreas],
    ["sourceSystem", answers.sourceSystem],
    ["floorplanVersionNote", answers.floorplanVersionNote],
    ["escapePlansStatus", answers.escapePlansStatus ? t(`onboardingPage.escape.status.${answers.escapePlansStatus}`) : undefined],
    ["escapePlansNote", answers.escapePlansNote],
  ];
  const hasGaps = c.missingSlots.length > 0;
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setArmed(true), SUBMIT_ARM_MS);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <>
      <StepBody>
        <StepHeading
          eyebrow={t("onboardingPage.nav.stepOf", { current: ctx.step.index, total: numberedCount(ctx.steps) })}
          title={t("onboardingPage.titles.review")}
          lead={t("onboardingPage.review.lead")}
          focus={ctx.focus}
        />
        <Collapse open={!!ctx.nudge}>
          <p role="status" className="rounded-xl bg-muted/60 px-4 py-3 text-sm">{ctx.nudge}</p>
        </Collapse>

        <section className="space-y-2" aria-label={t("onboardingPage.review.company")}>
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-sm font-semibold">{t("onboardingPage.review.company")}</h2>
            <ButtonTip label={t("onboardingPage.tips.editCompany")}><button type="button" onClick={() => edit(stepFor("company"))} className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground">{t("onboardingPage.review.edit")}</button></ButtonTip>
          </div>
          <p className="text-sm">{profile.companyName}{profile.legalName ? ` (${profile.legalName})` : ""}</p>
          <p className="text-sm text-muted-foreground">{[fullName(profile), profile.contactEmail].filter(Boolean).join(" · ")}</p>
          {view.phone?.number && (
            <p className="text-sm text-muted-foreground" data-testid="review-phone">
              {view.phone.number}
              {view.phone.verified && <span className="font-medium text-foreground"> ({t("onboardingPage.review.phoneVerified")})</span>}
            </p>
          )}
        </section>

        <section className="space-y-2" aria-label={t("onboardingPage.review.files")}>
          <h2 className="text-sm font-semibold">{t("onboardingPage.review.files")}</h2>
          <ul className="flex flex-wrap gap-2" data-testid="review-files">
            {dataSlots.map((slot) => {
              const count = filesOfSlot(view.files, slot.slot).length;
              const step = slot.required ? stepFor("slot", slot.slot) : optionalStep;
              const skipped = ctx.skipped.has(slot.slot) && count === 0;
              return (
                <li key={slot.slot}>
                  <ButtonTip label={t("onboardingPage.tips.editFiles", { slot: slotTitle(t, slot) })}>
                  <button
                    type="button"
                    onClick={() => edit(step)}
                    data-slot={slot.slot}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${count > 0 ? "border-primary/40" : "border-border text-muted-foreground"}`}
                  >
                    {slotTitle(t, slot)}
                    <span className="font-semibold">{count > 0 ? <Count value={count} /> : skipped ? t("onboardingPage.slot.skippedChip") : "–"}</span>
                  </button>
                  </ButtonTip>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="space-y-2" aria-label={t("onboardingPage.review.answers")}>
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-sm font-semibold">{t("onboardingPage.review.answers")}</h2>
            <ButtonTip label={t("onboardingPage.tips.editAnswers")}><button type="button" onClick={() => edit(stepFor("questions"))} className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground">{t("onboardingPage.review.edit")}</button></ButtonTip>
          </div>
          <dl className="grid grid-cols-[minmax(0,10rem)_1fr] gap-x-4 gap-y-1.5 text-sm">
            {answerRows.map(([key, value]) => (
              <div key={key} className="contents">
                <dt className="text-muted-foreground">{t(`onboardingPage.review.${key}`)}</dt>
                <dd className="min-w-0 whitespace-pre-wrap break-words">{value || <span className="text-muted-foreground">{t("onboardingPage.review.none")}</span>}</dd>
              </div>
            ))}
          </dl>
        </section>

        <Collapse open={hasGaps}>
          <p data-testid="missing-slots-hint" className="rounded-xl bg-muted/60 px-4 py-3 text-sm">{t("onboardingPage.review.gaps")}</p>
        </Collapse>

        <p role="note" className="flex items-start gap-3 rounded-2xl border border-border bg-white p-3 text-sm shadow-sm dark:bg-card" data-testid="submit-note">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="min-w-0 [overflow-wrap:anywhere]">
            {profile.contactEmail?.trim() ? t("onboardingPage.review.afterSubmit", { email: profile.contactEmail.trim() }) : t("onboardingPage.review.afterSubmitNoAddress")}
          </span>
        </p>

        <ResultTiles />
      </StepBody>
      <StepFooter
        onBack={ctx.back}
        onNext={onSubmit}
        nextIcon={false}
        nextLabel={<SubmitLabel submitting={c.submitting} />}
        nextDisabled={c.submitting || !armed}
        nextTip={t("onboardingPage.tips.submit")}
        nextReason={c.submitting ? t("onboardingPage.tips.submitting") : t("onboardingPage.tips.submitWait")}
        hint={c.submitError ?? t("onboardingPage.review.lock")}
      />
    </>
  );
}

export function SubmitLabel({ submitting }: { submitting: boolean }) {
  const { t } = useTranslation();
  return submitting ? (
    <span className="onb-fade inline-flex items-center">
      <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
      {t("onboardingPage.review.sending")}
    </span>
  ) : (
    <span className="onb-fade">{t("onboardingPage.review.submit")}</span>
  );
}

